'use client';

import { getProgressiveJackpots } from '@/lib/progressive-jackpots';
import { getDailyJackpots } from '@/lib/daily-jackpots';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { ProgressiveJackpotsBadge } from './layouts/progressive-jackpot/progressive-jackpot-badge';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { DailyJackpotBadge } from './layouts/daily-jackpot/daily-jackpot-badge';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '@/store/store';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';

export type BadgeFactoryClientProps = {
    feedsCode?: string;
    children: React.ReactNode;
    isSWJTickerDisplayed?: boolean;
    forceTextBadge?: boolean;
};

const BadgeFactoryClient = ({
    feedsCode,
    children,
    isSWJTickerDisplayed = false,
    forceTextBadge = false
}: BadgeFactoryClientProps) => {
    const debug = useStore((s) => s.debug);
    const { featureFlags } = useGlobalContext();

    // TODO: prioritize swj
    const isDailyJackpotsEnabled = featureFlags[PRODUCT_THROTTLES.DAILY_JACKPOTS];
    const dailyJackpotsQuery = useQuery({
        queryKey: ['fetch-daily-jackpots'],
        queryFn: async () => {
            const jackpots = await getDailyJackpots({
                state: debug.mockedState,
                cycle: debug.cycle,
                callback: debug.incrementDailyJackpotsCycle
            });

            return jackpots ? jackpots : [];
        },
        enabled: Boolean(feedsCode && isDailyJackpotsEnabled),
        refetchInterval: 30_000
    });

    const isProgressiveJackpotsEnabled = featureFlags[PRODUCT_THROTTLES.PROGRESSIVE_JACKPOTS];
    const progressiveJackpotsQuery = useQuery({
        queryKey: ['fetch-progressive-jackpots'],
        queryFn: async () => {
            const jackpots = await getProgressiveJackpots();

            return jackpots ? jackpots : [];
        },
        enabled: Boolean(feedsCode && isProgressiveJackpotsEnabled),
        refetchInterval: 30_000
    });

    if (!isSWJTickerDisplayed && !forceTextBadge) {
        if (dailyJackpotsQuery.data) {
            const foundDailyJackpot = dailyJackpotsQuery.data.find((jackpot) => `${jackpot.id}` === feedsCode);
            if (foundDailyJackpot && foundDailyJackpot.prevAmount && foundDailyJackpot.amount) {
                return <DailyJackpotBadge jackpot={foundDailyJackpot} />;
            }
        }

        if (progressiveJackpotsQuery.data) {
            const foundDailyJackpot = progressiveJackpotsQuery.data.find((jackpot) => `${jackpot.id}` === feedsCode);
            if (foundDailyJackpot && foundDailyJackpot.prevAmount && foundDailyJackpot.amount) {
                return <ProgressiveJackpotsBadge jackpot={foundDailyJackpot} />;
            }
        }
    }

    return children;
};

export default BadgeFactoryClient;
